
import * as React from 'react';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { Dialog, DialogFooter } from 'office-ui-fabric-react/lib/Dialog';
import { TextField, ITextField } from 'office-ui-fabric-react/lib/TextField';
import { PrimaryButton, DefaultButton } from 'office-ui-fabric-react/lib/Button';
import * as NameDialogStyles from './NameDialog.scss';
import strings from './NameDialog.resx';

/**
 * @internal
 */
export interface INameDialogProps {
  nameFieldAriaLabel?: string;
  title: string;
  continueText?: React.ReactNode;
  cancelText?: React.ReactNode;
  name: string;
  onContinue: (options: {
    name: string;
  }) => Promise<void>;
  onCancel: () => void;
  validator?: (folderName: string) => string | undefined;
  placeHolder?: string;
  maxLength?: number;
  cols?: number;
  isMultiline?: boolean;

  /**
   * The file extension to display to the right of the input box, if desired.
   */
  fileExtensionText?: string;
}

/**
 * @internal
 */
export interface INameDialogState {
  name: string;
  errorMessage?: string;
  showSpinner?: boolean;
}

class NameDialogBase extends React.PureComponent<INameDialogProps, INameDialogState> {
  private _isDisposed?: boolean;

  constructor(props: INameDialogProps) {
    super(props);

    this.state = {
      name: this.props.name
    };
  }

  public render(): JSX.Element {
    const {
      name,
      errorMessage,
      showSpinner
    } = this.state;

    const {
      nameFieldAriaLabel,
      continueText = strings.continue,
      cancelText,
      onCancel,
      placeHolder,
      maxLength,
      fileExtensionText,
      isMultiline,
      cols
    } = this.props;

    return (
      <Dialog
        isOpen={ true }
        title={ this.props.title }
        onDismiss={ onCancel }
      >
        <div className={ NameDialogStyles.nameDialogContent }>
          <TextField
            ariaLabel={ nameFieldAriaLabel }
            componentRef={ this._onNameRef }
            value={ name }
            onChanged={ this._onNameChanged }
            errorMessage={ errorMessage }
            placeholder={ placeHolder }
            maxLength={ maxLength }
            onKeyPress={ this._onKeyPress }
            multiline={ isMultiline }
            cols={ cols }
          />
          {
            fileExtensionText &&
            <span className={ NameDialogStyles.nameDialogContentFileExtension }>{ fileExtensionText }</span>
          }
        </div>
        <DialogFooter>
          <PrimaryButton
            disabled={ !name || !!errorMessage || showSpinner }
            onClick={ this._onClickAction }
          >
            { continueText }
          </PrimaryButton>
          { cancelText &&
            <DefaultButton
              onClick={ onCancel }
            >
              { cancelText }
            </DefaultButton>
          }
          { showSpinner && <Spinner size={ SpinnerSize.medium } className={ NameDialogStyles.nameDialogSpinner } /> }
        </DialogFooter>
      </Dialog>
    );
  }

  public componentWillUnmount(): void {
    this._isDisposed = true;
  }

  private _onNameRef = (textField: ITextField | null): void => {
    if (textField) {
      textField.focus();

      textField.select();
    }
  }

  private _onNameChanged = (value: string): void => {
    const {
      validator
    } = this.props;

    this.setState({
      name: value,
      errorMessage: validator ? validator(value) : undefined
    });
  }

  private _onClickAction = async (): Promise<void> => {
    this.setState({
      showSpinner: true
    });

    try {
      await this.props.onContinue({
        name: this.state.name
      });
    } catch (e) {
      const error: Error = e;

      if (!this._isDisposed) {
        this.setState({
          errorMessage: error && error.message
        });
      }
    } finally {
      if (!this._isDisposed) {
        this.setState({
          showSpinner: false
        });
      }
    }
  }

  private _onKeyPress = (evt: React.KeyboardEvent<HTMLElement>): void => {
    if (evt.which === 13 /* Enter */) {
      this._onClickAction();
    }
  }
}

/**
 * @internal
 */
export const NameDialog: React.StatelessComponent<INameDialogProps> = (props: INameDialogProps) => (<NameDialogBase { ...props } />);
